import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Crown, Lock, Shield, Heart, MessageCircle, Image, ThumbsUp, Smartphone } from 'lucide-react';
import SEO from '../../components/SEO';
import JsonLd from '../../components/JsonLd';

const ExploreCommunity = () => {
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  // Schema.org JSON-LD for Community Page - Maximum SEO
  const communitySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "PopCrown Community - Michael Jackson Fan Social Network",
    "description": "Join the PopCrown community - the ultimate Michael Jackson fan social network. Daily quizzes, global leaderboard, connect with MJ fans from 130+ countries. Compete, celebrate, and prove you're the biggest fan.",
    "url": "https://popcrownofficial.com/explore/community",
    "about": {
      "@type": "Person",
      "name": "Michael Jackson",
      "alternateName": "King of Pop"
    },
    "mainEntity": {
      "@type": "SocialMediaPosting",
      "headline": "PopCrown MJ Fan Community",
      "description": "Real Michael Jackson fans sharing Jackson 5 videos, MJ memorabilia collections, Off the Wall collectibles, Neverland Ranch discussions, dance class events"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://popcrownofficial.com"},
      {"@type": "ListItem", "position": 2, "name": "Explore", "item": "https://popcrownofficial.com/explore"},
      {"@type": "ListItem", "position": 3, "name": "Community", "item": "https://popcrownofficial.com/explore/community"}
    ]
  };

  // Real app screenshots showing the community - Enhanced alt text for SEO
  const communityScreenshots = [
    {
      src: 'https://customer-assets.emergentagent.com/job_49915189-438d-4b2d-92a3-08755161951c/artifacts/o3yy1wwd_IMG_0960.PNG',
      alt: 'PopCrown Michael Jackson fan community - Jo Day sharing Jackson 5 YouTube video memories - real MJ fans discussing King of Pop music and legacy in moderated safe space',
      caption: 'Fans sharing memories & videos'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_49915189-438d-4b2d-92a3-08755161951c/artifacts/abbk0t91_IMG_0961.PNG',
      alt: 'PopCrown MJ memorabilia collectors - Lesley Parkhouse wine glasses collection featuring Michael Jackson King of Pop themed merchandise and fan collectibles',
      caption: 'Collectors showcasing treasures'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_49915189-438d-4b2d-92a3-08755161951c/artifacts/dg77mlrj_IMG_0962.PNG',
      alt: 'PopCrown Michael Jackson fan events - mjjremembered MJ dance class Like MJ dance lessons tribute events meetups King of Pop choreography workshops',
      caption: 'Real events & meetups'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_49915189-438d-4b2d-92a3-08755161951c/artifacts/50oxxdzm_IMG_0963.PNG',
      alt: 'PopCrown Off the Wall album memorabilia - Lesley Parkhouse glass collection celebrating Michael Jackson 1979 breakthrough album MJ fan merchandise vintage',
      caption: 'Off the Wall love'
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_49915189-438d-4b2d-92a3-08755161951c/artifacts/p6xic57j_IMG_0964.PNG',
      alt: 'PopCrown Michael Jackson community discussions - MJFan1971 and Joe talking about Neverland Ranch MJ legacy King of Pop meaningful fan conversations',
      caption: 'Meaningful conversations'
    }
  ];

  const features = [
    { icon: <MessageCircle className="w-6 h-6" />, title: 'Community Feed', desc: 'Share posts, photos and videos' },
    { icon: <ThumbsUp className="w-6 h-6" />, title: 'Engagement', desc: 'Comments, likes, and @mentions' },
    { icon: <Users className="w-6 h-6" />, title: 'Follow System', desc: 'Build your fan network' },
    { icon: <Image className="w-6 h-6" />, title: 'Daily Poll', desc: 'Community-wide vote every day' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="community-page">
      <SEO
        title="PopCrown Community — MJ Fans from 130+ Countries | Leaderboard, Quizzes, Social"
        description="Join the PopCrown community — fans from 130+ countries competing on the global leaderboard, taking daily MJ quizzes, sharing memories, and celebrating Michael Jackson's legacy together."
        keywords="Michael Jackson fan community, PopCrown community, MJ social network, Michael Jackson fans, MJ leaderboard, MJ quiz, King of Pop fans, Michael Jackson discussion, MJ fan feed, fan points, Annie AI"
        canonical="/explore/community"
      />
      {/* Schema.org JSON-LD for Community Page */}
      <JsonLd data={communitySchema} />
      <JsonLd data={breadcrumbSchema} />

      {/* Header */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <Link to="/explore" className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" />
            Back to Explore
          </Link>
          <div className="flex items-center gap-4">
            <Users className="w-12 h-12 text-[#EC4899]" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                <span className="thriller-text">Michael Jackson Fan Community</span>
              </h1>
              <p className="text-gray-400">Where Michael Jackson fans compete, connect, and celebrate the King of Pop</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Vibe */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="p-8 rounded-xl text-center mb-12" style={{
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(225, 6, 0, 0.1) 100%)',
              border: '2px solid rgba(236, 72, 153, 0.3)'
            }}>
              <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-12 h-12 mx-auto mb-4 object-contain" />
              <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
                This Is Where the Real Fans Are
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-6">
                Daily quizzes. A global leaderboard. Fans from 130+ countries racing for pole position. Some think they've got what it takes — but their answers are totally <em>Off the Wall</em>. Think you can <em>Beat It</em> to the top?
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <span className="px-4 py-2 bg-white rounded-full text-gray-700 text-sm font-medium shadow-sm">🏆 Global Leaderboard</span>
                <span className="px-4 py-2 bg-white rounded-full text-gray-700 text-sm font-medium shadow-sm">🔥 Daily Challenges</span>
                <span className="px-4 py-2 bg-white rounded-full text-gray-700 text-sm font-medium shadow-sm">🌍 130+ Countries Competing</span>
              </div>
            </div>

            {/* Real Community Screenshots */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#EC4899]/10 text-[#EC4899] rounded-full text-sm font-semibold mb-4">
                <Smartphone className="w-4 h-4" />
                REAL SCREENSHOTS FROM THE APP
              </div>
              <h3 className="text-2xl font-bold text-black mb-2">See Our Community in Action</h3>
              <p className="text-gray-600">Real posts from real fans - this is what awaits you inside</p>
            </div>

            {/* Screenshot Gallery */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {communityScreenshots.slice(0, 3).map((screenshot, index) => (
                <div key={index} className="relative group" data-testid={`community-screenshot-${index}`}>
                  <div className="rounded-2xl overflow-hidden shadow-xl" style={{
                    border: '3px solid rgba(236, 72, 153, 0.3)'
                  }}>
                    <img 
                      src={screenshot.src} 
                      alt={screenshot.alt}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent rounded-b-2xl">
                    <p className="text-white text-sm font-medium text-center">{screenshot.caption}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Second Row - Larger */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
              {communityScreenshots.slice(3, 5).map((screenshot, index) => (
                <div key={index + 3} className="relative group" data-testid={`community-screenshot-${index + 3}`}>
                  <div className="rounded-2xl overflow-hidden shadow-xl" style={{
                    border: '3px solid rgba(236, 72, 153, 0.3)'
                  }}>
                    <img 
                      src={screenshot.src} 
                      alt={screenshot.alt}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent rounded-b-2xl">
                    <p className="text-white text-sm font-medium text-center">{screenshot.caption}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* What's Happening Inside */}
            <div className="p-6 rounded-xl mb-12" style={{ background: '#f8f8f8', border: '1px solid #eee' }}>
              <h4 className="font-bold text-gray-900 mb-4 text-center">What's happening inside right now:</h4>
              <div className="flex flex-wrap justify-center gap-3">
                <span className="px-3 py-1.5 bg-white rounded-full text-gray-700 text-sm shadow-sm">🎵 Music deep-dives</span>
                <span className="px-3 py-1.5 bg-white rounded-full text-gray-700 text-sm shadow-sm">🏆 Leaderboard battles</span>
                <span className="px-3 py-1.5 bg-white rounded-full text-gray-700 text-sm shadow-sm">📺 Video discoveries</span>
                <span className="px-3 py-1.5 bg-white rounded-full text-gray-700 text-sm shadow-sm">💃 Dance events</span>
                <span className="px-3 py-1.5 bg-white rounded-full text-gray-700 text-sm shadow-sm">🎬 Biopic countdown hype</span>
                <span className="px-3 py-1.5 bg-white rounded-full text-gray-700 text-sm shadow-sm">🏅 Fan Points & badges</span>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {features.map((feature, index) => (
                <div key={index} className="p-4 rounded-xl text-center" style={{
                  background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
                  border: '2px solid rgba(225, 6, 0, 0.2)'
                }}>
                  <div className="text-[#EC4899] mb-2 flex justify-center">{feature.icon}</div>
                  <h4 className="text-white font-semibold text-sm mb-1">{feature.title}</h4>
                  <p className="text-gray-400 text-xs">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MJVibe Review Quote */}
      <section className="py-12" style={{ background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="p-8 rounded-2xl text-center" style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              border: '2px solid rgba(225, 6, 0, 0.3)'
            }}>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E10600]/20 text-[#E10600] rounded-full text-xs font-semibold mb-4">
                INDEPENDENT REVIEW
              </div>
              <blockquote className="text-lg md:text-xl text-white mb-4 leading-relaxed" style={{ fontStyle: 'italic' }}>
                "The atmosphere is noticeably calmer than what many fans have grown used to elsewhere... PopCrown is a statement about ownership, dignity, and preservation."
              </blockquote>
              <div className="flex items-center justify-center gap-3 mb-4">
                <img 
                  src="https://www.mjvibe.com/wp-content/uploads/2021/01/MJVIBE-01-Logoa.png" 
                  alt="MJVibe Logo" 
                  className="h-6"
                />
                <span className="text-gray-400 text-sm">— MJVibe Review</span>
              </div>
              <a 
                href="https://www.mjvibe.com/popcrown-social-review-a-new-digital-sanctuary-for-michael-jackson-fans/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E10600] text-sm hover:underline"
              >
                Read Full Review →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-12 h-12 mx-auto mb-4 object-contain" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            <em>Don't Stop 'Til You Get Enough</em>
          </h2>
          <p className="text-gray-400 mb-4 max-w-xl mx-auto">
            The leaderboard is live. The quizzes are daily. Fans from 130+ countries are already competing. The only question is — are you <em>Bad</em> enough to join them?
          </p>
          <p className="text-[#E10600] mb-6 text-lg font-semibold">
            130+ Countries • Daily Quizzes • One Leaderboard • Your Move
          </p>
          <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block">
            Join PopCrown Community — £1.99/mo
          </a>
          <p className="text-gray-500 text-sm mt-4">£1.99 per month · Cancel anytime</p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-black">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs">
            Built by fans, for fans. PopCrown is not affiliated with any artist estates or rights holders.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExploreCommunity;
